/**
 * ChartDataInspector — what the server actually sent, as a table.
 *
 * A chart is a lossy view of its data: a series that draws nothing looks exactly
 * like a series that arrived empty, and an alias the server declined to answer
 * looks exactly like one whose values are all zero. This is the surface that
 * tells those apart, because every one of them has cost an afternoon at least
 * once.
 *
 * It reads the **decoded Arrow columns** — the `ChartGroupColumns` the transport
 * hands `ChartGroupView`, before `chart-feed` re-keys them onto plots. That is
 * deliberately upstream of the chart: the question this answers is "did the data
 * arrive", and asking the renderer would fold in every way the renderer can drop
 * it.
 *
 * Three things it says that the chart cannot:
 *
 * - **A requested alias with no column.** `bulkDeltas` skips an alias the
 *   response has no column for (`if (y)`), silently, so the plot simply never
 *   updates. Listed here as missing, in the one colour that means something is
 *   wrong.
 * - **The per-column summary.** Non-zero count, finite count, range. For a
 *   signal — a column that is mostly `0` by design — the non-zero count *is* the
 *   question, and reading it off 1500 rows by eye is not a thing anyone does.
 * - **The bar index.** Bars are evenly spaced with non-trading gaps collapsed, so
 *   a row's position in the loaded range is what the eye matches against; a
 *   marker that fired on bar 1203 and drew at bar 12 is only diagnosable against
 *   the index the row actually has.
 *
 * Colours are `var(--qf-*)` only, like every other component here.
 */

import { useMemo, useState } from "react";
import { plainText } from "@qufolio/screener-grid";

import {
  formatAge,
  formatBytes,
  formatMs,
  readTiming,
  type ChartTimings,
  type TimingSide,
} from "../lib/chart-timing";

/** The decoded columns, plus what was asked for — a copy, owned by the caller. */
export interface ChartDataSnapshot {
  chartGroupId: string;
  status: "ok" | "error";
  error?: string;
  symbol?: string;
  /** How to read {@link x} — unix seconds, or a synthetic row index. */
  xKind: "seconds" | "index";
  x: number[];
  columns: Record<string, number[]>;
  /**
   * Every alias the chart asked the server for. Held so a column that never came
   * back is a stated absence rather than a row that is not there.
   */
  requested: readonly string[];
  /**
   * What this chart's data cost to fetch and draw — the open, and the latest.
   *
   * Empty until the first load lands, and `{}` rather than optional so the
   * panel's one honest empty state is "the server sent no timings" rather than
   * two that read the same on screen.
   */
  timings: ChartTimings;
}

/** Base price columns, in the order a reader expects them rather than schema order. */
const BASE_ORDER = ["open", "high", "low", "close", "volume"];

/**
 * One pane's rendered geometry, as `SciChartJsChartBuilder.debugStats()` reports
 * it. Structural rather than imported: this is a diagnostic shape, and pinning
 * it to the builder's return type would make a debug panel a reason the chart
 * engine cannot change one.
 */
export interface PaneGeometry {
  paneId: string;
  seriesCount: number;
  xRange: { min: number; max: number };
  plot: { left: number; right: number };
  yAxes: Array<{ id: string; min: number; max: number }>;
  dataCounts: number[];
}

export interface ChartDataInspectorProps {
  snapshot: ChartDataSnapshot;
  /**
   * Live pane geometry. Empty before the surface is built.
   *
   * Here because two panes only line up if they agree on **both** their bar
   * window and their plot area in pixels, and neither is visible from the data
   * — a marker that sits beside its candle rather than under it is a disagreement
   * about one of those two numbers, and nothing in the columns can say which.
   */
  panes: readonly PaneGeometry[];
  /**
   * Bumped by the caller whenever the snapshot's arrays were appended to.
   *
   * They are mutated in place — the alternative is copying every column on every
   * realtime bar — so identity cannot drive the memos below and this stands in
   * for it. Nothing here reads the value; it is a dependency, not data.
   */
  stamp: number;
  onClose: () => void;
}

export function ChartDataInspector({
  snapshot,
  panes,
  stamp,
  onClose,
}: ChartDataInspectorProps) {
  const { x, columns, requested } = snapshot;

  // Base columns first, then whatever else the schema carried in its own order.
  // Sorting the tail alphabetically was the other option and is worse: the order
  // the server chose is information (it is the order the indicators were
  // requested in), and losing it costs more than an alphabet gains.
  const names = useMemo(() => {
    const rest = Object.keys(columns).filter((n) => !BASE_ORDER.includes(n));
    return [...BASE_ORDER.filter((n) => n in columns), ...rest];
  }, [columns]);

  const missing = useMemo(
    () => requested.filter((alias) => !(alias in columns)),
    [requested, columns],
  );

  const stats = useMemo(
    () => Object.fromEntries(names.map((n) => [n, summarise(columns[n] ?? [])])),
    [names, columns, stamp],
  );

  /** Restrict to the bars where one column fired. The whole point on a signal. */
  const [only, setOnly] = useState("");

  const rows = useMemo(() => {
    const all = x.map((_, i) => i);
    if (!only) return all;
    const col = columns[only];
    if (!col) return all;
    return all.filter((i) => {
      const v = col[i];
      return v !== undefined && Number.isFinite(v) && v !== 0;
    });
  }, [x, columns, only, stamp]);

  return (
    <div
      // A backdrop that closes on click, and a panel that does not — the table
      // is wide and a stray click inside it must not throw the reading away.
      onPointerDown={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2000,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "3vh 3vw",
      }}
    >
      <div
        role="dialog"
        aria-label="Chart data"
        onPointerDown={(e) => e.stopPropagation()}
        style={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          height: "100%",
          minHeight: 0,
          background: "var(--qf-surface)",
          border: "1px solid var(--qf-border)",
          borderRadius: 8,
          boxShadow: "0 10px 40px rgba(0,0,0,0.45)",
          color: "var(--qf-text)",
        }}
      >
        <Header snapshot={snapshot} rows={rows.length} onClose={onClose} />

        {missing.length > 0 && (
          <div
            style={{
              padding: "0.4rem 0.9rem",
              fontSize: 12,
              color: "var(--qf-negative)",
              borderBottom: "1px solid var(--qf-border)",
            }}
          >
            Requested but not returned: <b>{missing.join(", ")}</b> — the server sent no column
            for {missing.length === 1 ? "it" : "them"}, so the plot never receives data.
          </div>
        )}

        <Timing timings={snapshot.timings} />

        <Geometry panes={panes} />

        <Summary names={names} stats={stats} />

        <Controls
          names={names}
          only={only}
          onOnly={setOnly}
          onCopy={() => void copyCsv(snapshot, names, rows)}
        />

        <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
          <table
            style={{
              borderCollapse: "collapse",
              fontSize: 11.5,
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              fontVariantNumeric: "tabular-nums",
              whiteSpace: "nowrap",
            }}
          >
            <thead>
              <tr>
                <Th sticky>#</Th>
                <Th sticky>{snapshot.xKind === "seconds" ? "time (UTC)" : "x"}</Th>
                {names.map((n) => (
                  <Th key={n} sticky>
                    {n}
                  </Th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((i) => (
                <tr key={i}>
                  <Td dim>{i}</Td>
                  <Td dim>{formatX(x[i], snapshot.xKind)}</Td>
                  {names.map((n) => (
                    <Td key={n}>{formatValue(columns[n]?.[i])}</Td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === 0 && (
            <div style={{ padding: "1rem", fontSize: 12, color: "var(--qf-text-secondary)" }}>
              No rows. {only ? `Nothing non-zero in "${only}".` : "The load carried no bars."}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Header({
  snapshot,
  rows,
  onClose,
}: {
  snapshot: ChartDataSnapshot;
  rows: number;
  onClose: () => void;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "0.9rem",
        padding: "0.55rem 0.9rem",
        borderBottom: "1px solid var(--qf-border)",
        fontSize: 12,
      }}
    >
      <b style={{ fontSize: 13 }}>{snapshot.symbol ?? "—"}</b>
      <Fact label="bars" value={String(snapshot.x.length)} />
      <Fact label="showing" value={String(rows)} />
      <Fact label="columns" value={String(Object.keys(snapshot.columns).length)} />
      <Fact label="x" value={snapshot.xKind} />
      <Fact
        label="status"
        value={snapshot.status}
        tone={snapshot.status === "ok" ? undefined : "var(--qf-negative)"}
      />
      {snapshot.error && <span style={{ color: "var(--qf-negative)" }}>{snapshot.error}</span>}
      <span style={{ flex: 1 }} />
      <button type="button" onClick={onClose} style={buttonStyle}>
        Close
      </button>
    </div>
  );
}

function Fact({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <span style={{ color: "var(--qf-text-secondary)" }}>
      {label} <b style={{ color: tone ?? "var(--qf-text)" }}>{value}</b>
    </span>
  );
}

/**
 * Where the time went, from the request leaving to the bars being drawn.
 *
 * The panel above answers "did the data arrive". This answers "why did it take
 * that long", which used to be answerable only with a stopwatch and a guess —
 * a chart open crosses a dozen stages on two machines and none of them was
 * measured on the path a user actually takes.
 *
 * **Two readings, and they are not the same question.** *Open* is the first
 * bulk load, the cost somebody actually felt; *latest* is the most recent
 * 60-second refresh. Keeping only the second means the open is gone within a
 * minute of every load, and keeping only the first means a chart left running
 * says nothing about what it is doing now.
 *
 * Every colour is a `var(--qf-*)` token, like everything else here.
 */
function Timing({ timings }: { timings: ChartTimings }) {
  const [showing, setShowing] = useState<"open" | "latest">("open");
  const record = showing === "open" ? timings.open : timings.latest;
  const other = showing === "open" ? timings.latest : timings.open;

  if (!record) {
    return (
      <div style={SECTION}>
        <span style={{ color: "var(--qf-text-secondary)" }}>
          No timings yet — nothing has loaded on this chart.
        </span>
      </div>
    );
  }

  const reading = readTiming(record);
  // The server's own share is what a reader wants first: it is the half nobody
  // can see from the browser, and it is usually most of the number.
  //
  // Everything the server spent on the request: what it waited through before
  // the build (sign-in, a worker) as well as the build. The breakdown line under
  // the header keeps the build's own number in view.
  const serverAllMs = reading.beforeBuildMs + reading.serverMs;
  const serverShare =
    reading.totalMs > 0 ? Math.round((serverAllMs / reading.totalMs) * 100) : 0;

  return (
    <div style={SECTION}>
      <div style={{ display: "flex", alignItems: "center", gap: "0.9rem", marginBottom: "0.45rem" }}>
        <b>Timing</b>
        <Fact label="total" value={formatMs(reading.totalMs)} />
        <Fact label="server" value={`${formatMs(serverAllMs)} · ${serverShare}%`} />
        <Fact
          label="wire"
          value={
            // Null on a refresh, and said rather than blanked: the client stamps
            // from its last `send` and a refresh is a push it never asked for,
            // so the only honest answer is that there is nothing to measure.
            reading.wireMs === null ? "n/a on a refresh" : `≈ ${formatMs(reading.wireMs)}`
          }
        />
        <Fact label="client" value={formatMs(reading.clientMs)} />
        <Fact label="payload" value={formatBytes(reading.bytes)} />
        {reading.groupCount > 1 && (
          // One socket carries one request per layout, so the wire and the
          // decode above were paid for this many charts at once — and the
          // server's whole response is longer than this chart's share of it.
          // Both numbers are shown because the gap *is* the other charts, and
          // charging it here would be the panel's biggest available lie.
          <Fact
            label={`${reading.groupCount} charts share this response, together`}
            value={formatMs(reading.bundleMs)}
          />
        )}
        <span style={{ flex: 1 }} />
        {other && (
          <button
            type="button"
            onClick={() => setShowing(showing === "open" ? "latest" : "open")}
            style={buttonStyle}
          >
            {showing === "open" ? "Show latest refresh" : "Show the open"}
          </button>
        )}
      </div>

      {reading.beforeBuild.length > 0 && (
        // Only when the server reported a wait, so an older server's panel
        // reads exactly as it did.
        <div style={{ color: "var(--qf-text-secondary)", marginBottom: "0.3rem" }}>
          {"server = "}
          {[
            ...reading.beforeBuild.map((row) => `${row.name} ${formatMs(row.ms)}`),
            `build ${formatMs(reading.serverMs)}`,
          ].join(" · ")}
        </div>
      )}

      <div style={{ color: "var(--qf-text-secondary)", marginBottom: "0.45rem" }}>
        {showing === "open" ? "First load" : "Most recent frame"} ·{" "}
        {formatAge(record.at, Date.now())}
        {record.server ? (
          <>
            {" · "}
            {record.server.source === "refresh"
              ? "a channel rebuild, shared with every subscriber"
              : "this chart's own request"}
            {record.server.host && ` · ${record.server.host}`}
            {record.server.phase && ` · market ${record.server.phase.replace("_", " ")}`}
          </>
        ) : (
          " · the server sent no timings"
        )}
      </div>

      <StageBar reading={reading} />

      <table
        style={{
          borderCollapse: "collapse",
          fontVariantNumeric: "tabular-nums",
          marginTop: "0.45rem",
        }}
      >
        <tbody>
          {reading.rows.map((row, i) => (
            <tr key={`${row.name}-${i}`}>
              <Td dim>
                <span
                  style={{
                    display: "inline-block",
                    width: 8,
                    height: 8,
                    borderRadius: 2,
                    marginRight: 6,
                    background: toneFor(row.side),
                  }}
                />
                {row.name}
              </Td>
              <Td>{formatMs(row.ms)}</Td>
              <Td dim>{row.fraction > 0 ? `${Math.round(row.fraction * 100)}%` : ""}</Td>
              <td
                style={{
                  padding: "0.15rem 0.6rem",
                  color: "var(--qf-text-muted)",
                  whiteSpace: "nowrap",
                }}
              >
                {plainText(row.detail) ?? ""}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {record.server?.shared.map((read) => (
        <div key={read.symbol} style={{ marginTop: "0.35rem", color: "var(--qf-text-secondary)" }}>
          <b style={{ color: "var(--qf-text)" }}>{read.symbol}</b>{" "}
          read {formatMs(read.readMs)}
          {read.realtimeMs !== undefined && ` · session bar ${formatMs(read.realtimeMs)}`}
          {read.rtKey && (
            <>
              {" from "}
              <code>{read.rtKey}</code>
              {/* Warm and cold differ by tens of milliseconds and the
                  difference is the poller doing its job, so it is named
                  rather than left to be inferred from the duration. */}
              <span style={{ color: read.rtWarm ? "var(--qf-positive)" : "var(--qf-warn)" }}>
                {read.rtWarm ? " (cached)" : " (fetched)"}
              </span>
            </>
          )}
          {read.error && <span style={{ color: "var(--qf-negative)" }}> · {read.error}</span>}
          {/* Said once, here, because it is the one number on this panel that a
              two-chart layout would otherwise be read as paying twice. */}
          <span style={{ color: "var(--qf-text-muted)" }}> — read once for every chart on it</span>
        </div>
      ))}
    </div>
  );
}

/** A stage's colour by which side of the wire it happened on. */
function toneFor(side: TimingSide): string {
  switch (side) {
    case "server":
      return "var(--qf-accent)";
    case "wire":
      return "var(--qf-warn)";
    case "unattributed":
      // Not `negative`: nothing is wrong, it is simply time nobody has named
      // yet, and red for an ordinary gap teaches people to distrust the red.
      return "var(--qf-text-muted)";
    default:
      return "var(--qf-positive)";
  }
}

/**
 * The whole request as one bar, proportionally.
 *
 * A table of durations makes the biggest number findable; only a bar makes it
 * *obvious*, which is the difference between a reader who checks and a reader
 * who notices. Segments under a pixel are still drawn — a zero-width flex child
 * simply vanishes, and a stage disappearing because it was fast reads exactly
 * like a stage that did not run.
 */
function StageBar({ reading }: { reading: ReturnType<typeof readTiming> }) {
  if (reading.totalMs <= 0) {
    return (
      <div style={{ color: "var(--qf-text-secondary)" }}>
        Every stage measured zero — this is the offline fixture, not a live server.
      </div>
    );
  }
  return (
    <div
      style={{
        display: "flex",
        height: 10,
        borderRadius: 3,
        overflow: "hidden",
        border: "1px solid var(--qf-border)",
      }}
    >
      {reading.rows
        .filter((row) => row.ms > 0)
        .map((row, i) => (
          <div
            key={`${row.name}-${i}`}
            title={`${row.name} — ${formatMs(row.ms)}`}
            style={{
              flexGrow: row.ms,
              flexBasis: 0,
              minWidth: 1,
              background: toneFor(row.side),
              // The alternating dim keeps two adjacent server stages apart
              // without a second colour ramp nobody could read a legend for.
              opacity: i % 2 === 0 ? 1 : 0.65,
            }}
          />
        ))}
    </div>
  );
}

const SECTION: React.CSSProperties = {
  padding: "0.5rem 0.9rem",
  borderBottom: "1px solid var(--qf-border)",
  fontSize: 11.5,
};

/**
 * Per-pane geometry, and whether the panes actually agree.
 *
 * Two panes draw the same bar at the same screen x only if they agree on the
 * **bar window** (`xRange`, mirrored by `syncXAxes`) *and* on the **plot area**
 * in pixels (each pane is inset by its own Y axis, which is why every axis is
 * pinned to one `axisThickness`). A marker that sits beside its candle instead
 * of under it means one of those two is wrong, and the columns cannot say which
 * — so the check is here rather than left as an eye-test on two rows of numbers.
 *
 * A mismatched **width** with a matching left edge is the interesting case: the
 * error is then proportional, near zero at the left of the window and worst at
 * the right, which is what a collapsed axis looks like from the outside.
 */
function Geometry({ panes }: { panes: readonly PaneGeometry[] }) {
  if (panes.length === 0) return null;

  const first = panes[0]!;
  const disagree = (pick: (p: PaneGeometry) => number): boolean =>
    // Rounded: sub-pixel layout differences are not a misalignment anyone can
    // see, and flagging them would make the check cry wolf on every chart.
    panes.some((p) => Math.round(pick(p)) !== Math.round(pick(first)));

  const badLeft = disagree((p) => p.plot.left);
  const badWidth = disagree((p) => p.plot.right - p.plot.left);
  const badRange = disagree((p) => p.xRange.min) || disagree((p) => p.xRange.max);

  return (
    <div
      style={{
        padding: "0.5rem 0.9rem",
        borderBottom: "1px solid var(--qf-border)",
        fontSize: 11.5,
      }}
    >
      <table style={{ borderCollapse: "collapse", fontVariantNumeric: "tabular-nums" }}>
        <thead>
          <tr style={{ color: "var(--qf-text-secondary)" }}>
            {["pane", "plot left", "plot right", "width", "bars from", "to", "y axes"].map((h) => (
              <th key={h} style={{ padding: "0.1rem 0.6rem", textAlign: "right", fontWeight: 600 }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {panes.map((p) => (
            <tr key={p.paneId}>
              <Td dim>…{p.paneId.slice(-6)}</Td>
              <Td tone={badLeft ? "var(--qf-negative)" : undefined}>{round(p.plot.left)}</Td>
              <Td>{round(p.plot.right)}</Td>
              <Td tone={badWidth ? "var(--qf-negative)" : undefined}>
                {round(p.plot.right - p.plot.left)}
              </Td>
              <Td tone={badRange ? "var(--qf-negative)" : undefined}>{round(p.xRange.min)}</Td>
              <Td tone={badRange ? "var(--qf-negative)" : undefined}>{round(p.xRange.max)}</Td>
              <Td dim>{p.yAxes.map((a) => a.id || "(default)").join(", ")}</Td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ marginTop: "0.35rem", color: "var(--qf-text-secondary)" }}>
        {badLeft || badWidth ? (
          <span style={{ color: "var(--qf-negative)" }}>
            Plot areas differ — panes cannot align.{" "}
            {badWidth && !badLeft
              ? "Same left edge, different width: the error grows toward the right of the window. A pane's Y axis is not taking the width every other pane's does."
              : "Different left edge: every bar is off by a constant."}
          </span>
        ) : badRange ? (
          <span style={{ color: "var(--qf-negative)" }}>
            Panes are showing different bar windows — the X range sync is not holding.
          </span>
        ) : (
          "Plot areas and bar windows agree — panes are aligned, so any offset is inside the series."
        )}
      </div>
    </div>
  );
}

function round(value: number): string {
  return Number.isFinite(value) ? String(Math.round(value)) : "—";
}

/**
 * One line per column: how many values, how many of them mean anything.
 *
 * `nonZero` is first because it is the number that settles the common question.
 * A signal column reads `1500 values · 43 non-zero · +28 / −15`, which says the
 * server computed it, how often it fired, and which way — three answers that
 * otherwise need the whole table read.
 */
function Summary({
  names,
  stats,
}: {
  names: string[];
  stats: Record<string, ColumnStats>;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "0.35rem 1.1rem",
        padding: "0.5rem 0.9rem",
        borderBottom: "1px solid var(--qf-border)",
        fontSize: 11.5,
        maxHeight: "22vh",
        overflow: "auto",
      }}
    >
      {names.map((n) => {
        const s = stats[n];
        if (!s) return null;
        return (
          <span key={n} style={{ color: "var(--qf-text-secondary)" }}>
            <b style={{ color: "var(--qf-text)" }}>{n}</b>{" "}
            <span style={{ color: s.nonZero === 0 ? "var(--qf-negative)" : undefined }}>
              {s.nonZero} non-zero
            </span>
            {s.signed && ` · +${s.positive} / −${s.negative}`}
            {s.nan > 0 && ` · ${s.nan} NaN`}
            {s.finite > 0 && ` · ${formatValue(s.min)}…${formatValue(s.max)}`}
          </span>
        );
      })}
    </div>
  );
}

function Controls({
  names,
  only,
  onOnly,
  onCopy,
}: {
  names: string[];
  only: string;
  onOnly: (name: string) => void;
  onCopy: () => void;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "0.6rem",
        padding: "0.45rem 0.9rem",
        borderBottom: "1px solid var(--qf-border)",
        fontSize: 12,
      }}
    >
      <label style={{ color: "var(--qf-text-secondary)" }}>
        Only rows where{" "}
        <select value={only} onChange={(e) => onOnly(e.target.value)} style={selectStyle}>
          <option value="">(show all)</option>
          {names.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>{" "}
        ≠ 0
      </label>
      <span style={{ flex: 1 }} />
      <button type="button" onClick={onCopy} style={buttonStyle}>
        Copy CSV
      </button>
    </div>
  );
}

interface ColumnStats {
  finite: number;
  nan: number;
  nonZero: number;
  positive: number;
  negative: number;
  min: number;
  max: number;
  /** Every finite value is −1, 0 or +1 — so the ± split is worth printing. */
  signed: boolean;
}

function summarise(values: readonly number[]): ColumnStats {
  let finite = 0;
  let nan = 0;
  let nonZero = 0;
  let positive = 0;
  let negative = 0;
  let min = Infinity;
  let max = -Infinity;
  let signed = true;

  for (const v of values) {
    if (v === undefined || !Number.isFinite(v)) {
      nan++;
      continue;
    }
    finite++;
    if (v < min) min = v;
    if (v > max) max = v;
    if (v !== 0) {
      nonZero++;
      if (v > 0) positive++;
      else negative++;
    }
    if (v !== 0 && v !== 1 && v !== -1) signed = false;
  }

  return {
    finite,
    nan,
    nonZero,
    positive,
    negative,
    min: finite ? min : NaN,
    max: finite ? max : NaN,
    // A column of all zeros is not a signal, it is an absence — calling it
    // "signed" would print `+0 / −0` and read as a finding.
    signed: signed && nonZero > 0,
  };
}

/**
 * UTC, always, and labelled as such.
 *
 * A local-time rendering here would disagree with the bar the server computed
 * against, which is precisely the disagreement this table exists to rule out —
 * and it is the same off-by-one `chart-window.ts` already carries a note about.
 */
export function formatX(value: number | undefined, kind: "seconds" | "index"): string {
  if (value === undefined) return "";
  if (kind === "index") return String(value);

  // **`toISOString` throws on an invalid Date**, and it threw here — "Invalid
  // time value", taking the whole app down with it, because a React render that
  // raises unmounts the tree. The panel that exists to show what the server
  // actually sent was the one thing that could not survive being sent something
  // strange.
  //
  // Two ways in, and `Number.isFinite` only catches the first: a null decoded as
  // NaN (`chart/arrow.ts` maps nulls to NaN on purpose), and a number that is
  // finite but past the ±8.64e15 ms a Date can hold — a microsecond or
  // nanosecond timestamp read as milliseconds does exactly that.
  //
  // The bad value is *shown* rather than blanked. This is the inspector: a
  // column of empty cells would hide the very thing someone opened it to find.
  const at = new Date(value * 1000);
  if (Number.isNaN(at.getTime())) return `⚠ ${formatValue(value)}`;

  const iso = at.toISOString();
  // Midnight is a daily bar and the time half is noise; anything else is intraday.
  return iso.endsWith("T00:00:00.000Z") ? iso.slice(0, 10) : iso.slice(0, 16).replace("T", " ");
}

function formatValue(value: number | undefined): string {
  if (value === undefined) return "";
  if (Number.isNaN(value)) return "NaN";
  if (!Number.isFinite(value)) return value > 0 ? "∞" : "−∞";
  if (Number.isInteger(value)) return String(value);
  return value.toFixed(4);
}

async function copyCsv(
  snapshot: ChartDataSnapshot,
  names: string[],
  rows: readonly number[],
): Promise<void> {
  const head = ["index", snapshot.xKind === "seconds" ? "time" : "x", ...names].join(",");
  const body = rows.map((i) =>
    [
      i,
      formatX(snapshot.x[i], snapshot.xKind),
      ...names.map((n) => {
        const v = snapshot.columns[n]?.[i];
        return v === undefined || Number.isNaN(v) ? "" : String(v);
      }),
    ].join(","),
  );
  try {
    await navigator.clipboard.writeText([head, ...body].join("\n"));
  } catch {
    // Clipboard needs a secure context and a user gesture; both hold here, but a
    // denied permission is a real answer and not worth a thrown dialog.
    // eslint-disable-next-line no-console
    console.warn("[chart] clipboard write refused");
  }
}

function Th({ children, sticky }: { children: React.ReactNode; sticky?: boolean }) {
  return (
    <th
      style={{
        position: sticky ? "sticky" : undefined,
        top: 0,
        zIndex: 1,
        background: "var(--qf-surface)",
        borderBottom: "1px solid var(--qf-border)",
        padding: "0.3rem 0.6rem",
        textAlign: "right",
        fontWeight: 600,
        color: "var(--qf-text-secondary)",
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  dim,
  tone,
}: {
  children: React.ReactNode;
  dim?: boolean;
  /** Overrides the normal colour — used to mark a number that disagrees. */
  tone?: string;
}) {
  return (
    <td
      style={{
        padding: "0.15rem 0.6rem",
        textAlign: "right",
        color: tone ?? (dim ? "var(--qf-text-secondary)" : "var(--qf-text)"),
        fontWeight: tone ? 700 : undefined,
      }}
    >
      {children}
    </td>
  );
}

const buttonStyle: React.CSSProperties = {
  font: "inherit",
  fontSize: 12,
  padding: "0.2rem 0.6rem",
  borderRadius: 4,
  border: "1px solid var(--qf-border)",
  background: "var(--qf-background)",
  color: "var(--qf-text)",
  cursor: "pointer",
};

const selectStyle: React.CSSProperties = {
  font: "inherit",
  fontSize: 12,
  padding: "0.1rem 0.3rem",
  borderRadius: 4,
  border: "1px solid var(--qf-border)",
  background: "var(--qf-background)",
  color: "var(--qf-text)",
};
